import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { showMessage } from '../../../helpers/MessageUtility';
import CameraIcon from '../../../icons/CameraIcon';
import GlobalStreamManager from '../../../helpers/GlobalStreamManager';
import { OCR_CONSTANTS, OCR_MESSAGES } from './constants';
import { convertCanvasToFile } from './utils';
import './OcrImagePickerModal.scss';

export interface OcrCameraProps {
  onImageCaptured: (imageSrc: string, file: File) => void;
  onCameraReady?: (stopCamera: () => void) => void;
}

export default function OcrCamera({ onImageCaptured, onCameraReady }: OcrCameraProps) {
  const [cameraReady, setCameraReady] = useState(false);
  const [isInitializingCamera, setIsInitializingCamera] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const stopCamera = useCallback(() => {
    GlobalStreamManager.close();
    setCameraReady(false);
    setIsInitializingCamera(false);
  }, []);

  const startCamera = useCallback(async () => {
    if (isInitializingCamera) return;

    try {
      setIsInitializingCamera(true);

      const stream = await GlobalStreamManager.open({
        video: OCR_CONSTANTS.CAMERA_CONFIG,
        audio: false
      });

      setTimeout(() => {
        if (videoRef.current && stream) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true;

          const handleVideoLoad = () => {
            if (videoRef.current && videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
              setCameraReady(true);
              setIsInitializingCamera(false);
            }
          };

          const handleVideoError = () => {
            console.error('Video playback error');
            setIsInitializingCamera(false);
            showMessage('error', 'Camera video playback failed');
          };

          const metadataTimeout = setTimeout(() => {
            if (!cameraReady && videoRef.current) {
              console.warn('Video metadata loading timeout, attempting to continue');
              setCameraReady(true);
              setIsInitializingCamera(false);
            }
          }, 5000);

          videoRef.current.onloadedmetadata = () => {
            clearTimeout(metadataTimeout);
            handleVideoLoad();
          };
          videoRef.current.onerror = handleVideoError;

          videoRef.current.play().catch((playError) => {
            console.error('Video play error:', playError);
            setTimeout(() => {
              videoRef.current?.play().catch((retryError) => {
                console.error('Video play retry error:', retryError);
                setIsInitializingCamera(false);
                showMessage('error', 'Failed to start camera video');
              });
            }, 100);
          });
        }
      }, 200);
    } catch (err: any) {
      let errorMessage: string;

      if (err.name === 'NotAllowedError') {
        errorMessage = OCR_MESSAGES.CAMERA_PERMISSION_ERROR;
      } else {
        errorMessage = 'An unexpected error occurred while initializing the camera.';
      }

      showMessage('Error', errorMessage);
      setIsInitializingCamera(false);
    }
  }, [isInitializingCamera, cameraReady]);

  const takePhoto = useCallback(() => {
    if (videoRef.current && GlobalStreamManager.stream) {
      const imageData = GlobalStreamManager.capture(videoRef.current);

      // Convert to file using existing utility
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context && videoRef.current) {
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

        convertCanvasToFile(canvas).then((file) => {
          stopCamera();
          onImageCaptured(imageData, file);
        });
      }
    } else {
      showMessage('error', OCR_MESSAGES.CAMERA_NOT_READY);
    }
  }, [stopCamera, onImageCaptured]);

  useEffect(() => {
    startCamera();

    // Expose the stopCamera function to parent
    if (onCameraReady) {
      onCameraReady(stopCamera);
    }

    return () => {
      stopCamera();
    };
  }, []);

  return (
    <div className="ocr-camera-container">
      {isInitializingCamera && (
        <div className="ocr-camera-loading">
          <Icon name="spinner" loading size="big" />
          <p>{OCR_MESSAGES.INITIALIZING_CAMERA}</p>
        </div>
      )}
      <video
        ref={videoRef}
        className="ocr-camera-video"
        autoPlay
        playsInline
        muted
        style={{ display: isInitializingCamera || !cameraReady ? 'none' : 'block' }}
      />
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      {cameraReady && !isInitializingCamera && <Button className="ocr-capture-button" icon={<CameraIcon />} circular size="huge" onClick={takePhoto} />}
    </div>
  );
}
